// Type-only: pins the settings tab list to the customer enum without a runtime import from a
// feature into `lib`.
import type { OrganizationTab } from "@/features/enterprise-sidebar/_logic/enums";
import { ADMIN_LAUNCH_PARAM, ADMIN_ORG_ID_PARAM } from "@/features/create-event-v3/_logic/constants";
import { ENTERPRISE_REGISTRATION_TABS, type EnterpriseRegistrationTab } from "@/lib/routes/registration-paths";

/**
 * The admin dashboard v2 route scheme — owned here rather than by the shell feature so the app
 * router segments, the shell's static nav configs and the route-map test all read one source.
 *
 * Two flavours of href live side by side, mirroring `registration-paths.ts`:
 * - **Templated hrefs** carry the literal `{orgId}` / `{eventId}` placeholders, so a static nav
 *   config can be declared once at module scope and resolved per render via
 *   {@link replaceAdminIds}.
 * - **Builder functions** take numeric ids and return a concrete path, for direct links.
 *
 * {@link ADMIN_DASH_ROUTES} lists every concrete route pattern with Next.js dynamic segment
 * names, so a test can walk `src/app/admin/v2` and prove the filesystem and the map agree.
 */

/** Root of the admin dashboard v2 segment. Inherits the admin gate from `src/app/admin/layout.tsx`. */
export const ADMIN_V2_ROOT = "/admin/v2";

// ---------------------------------------------------------------------------
// All-orgs scope
// ---------------------------------------------------------------------------

/** All-orgs Orgs overview. */
export const ADMIN_ORGS_PATH = `${ADMIN_V2_ROOT}/orgs`;

/** Metrics' home inside the shell. Redirects to {@link ADMIN_METRICS_DEFAULT_TAB}. */
export const ADMIN_METRICS_PATH = `${ADMIN_V2_ROOT}/metrics`;

/**
 * Metrics tab ids that carry a real screen, each its own static route folder under
 * `/admin/v2/metrics` — mirroring event sourcing's `search`/`proposals`/`contracts`/`payments`
 * folders rather than a single `[tab]` page, since each tab is a genuinely distinct screen to be
 * ported in, not one placeholder shown under different names.
 *
 * Mirrors `/admin/metrics`'s enabled tabs (Finance, Pipeline, Self-serve, Extractions) — the
 * three "coming soon" tabs on that page (Enterprise, Mid-market, Capacity) have no view behind
 * them, so they stay disabled rail rows rather than routes; a route with nothing to render at the
 * end of it is worse than no route.
 */
export const ADMIN_METRICS_TABS = ["finance", "pipeline", "self-serve", "extractions", "planner-bot", "fetch"] as const;

/** A valid `/admin/v2/metrics/[tab]` segment. */
export type AdminMetricsTab = (typeof ADMIN_METRICS_TABS)[number];

/** The Venues section of Metrics, which groups the tabs about hotels on the portal. */
export const ADMIN_METRICS_VENUES_PATH = `${ADMIN_METRICS_PATH}/venues`;

/** Tabs under {@link ADMIN_METRICS_VENUES_PATH}, in rail order. */
export const ADMIN_METRICS_VENUES_TABS = ["messaging", "portal-claims"] as const;

/** A valid `/admin/v2/metrics/venues/[tab]` segment. */
export type AdminMetricsVenuesTab = (typeof ADMIN_METRICS_VENUES_TABS)[number];

/** Tab the Metrics base route redirects to. */
export const ADMIN_METRICS_DEFAULT_TAB: AdminMetricsTab = "finance";

/** Planning's home inside the shell. Redirects to {@link ADMIN_PLANNING_DEFAULT_TAB}. */
export const ADMIN_PLANNING_PATH = `${ADMIN_V2_ROOT}/planning`;

/**
 * Planning tab ids that carry a real screen, each its own static route folder under
 * `/admin/v2/planning` — mirroring {@link ADMIN_METRICS_TABS}. The Event Readiness Console has
 * three more screens (Premier ledger, Pricing, System state) with no view behind them yet, so
 * they stay disabled rail rows rather than routes with nothing to render at the end of them.
 */
export const ADMIN_PLANNING_TABS = ["attention", "readiness", "people"] as const;

/** A valid `/admin/v2/planning/[tab]` segment. */
export type AdminPlanningTab = (typeof ADMIN_PLANNING_TABS)[number];

/** Tab the Planning base route redirects to. */
export const ADMIN_PLANNING_DEFAULT_TAB: AdminPlanningTab = "readiness";

/** All-orgs Events overview ("My events"). Also the landing destination for `/admin/v2`. */
export const ADMIN_EVENTS_PATH = `${ADMIN_V2_ROOT}/events`;

/** All-orgs Triage list. */
export const ADMIN_EVENTS_TRIAGE_PATH = `${ADMIN_V2_ROOT}/events/triage`;

/** All-orgs Tasks list. */
export const ADMIN_TASKS_PATH = `${ADMIN_V2_ROOT}/tasks`;

/** All-orgs Users list. */
export const ADMIN_ALL_ORGS_USERS_PATH = `${ADMIN_V2_ROOT}/users`;

/** All-orgs Users detail href template. */
export const ADMIN_USER_HREF = `${ADMIN_ALL_ORGS_USERS_PATH}/{userId}`;

/** All-orgs Archive — a card grid of lower-priority legacy `/admin/*` tools, linked out rather than ported in. */
export const ADMIN_ARCHIVE_PATH = `${ADMIN_V2_ROOT}/archive`;

/** Where every admin-dash "Create event" button goes — the same destination the customer rail
 *  uses. Not exported: link to it only via {@link adminCreateEventHref}, never directly, so an
 *  admin-dash entry point can't accidentally drop the `admin_org_id`/`admin_launch` marker that
 *  tells `/create` this is an admin-initiated launch rather than the admin using the product as
 *  themselves. */
const ADMIN_CREATE_EVENT_PATH = "/create";

/**
 * href for an admin-dash "Create event" button (ENG-918) — the one place that knows how to mark a
 * `/create` link as admin-launched. `admin_org_id` when the org is already known (`OrgNav`'s
 * per-org button, the org-scoped Events page); otherwise `admin_launch=1` (e.g. `AdminAllEventsPage`'s
 * button, which spans every org and has no single one to attach), so the visit still reaches the
 * org-picker step instead of silently falling through to the normal customer wizard for lack of any
 * admin-flow signal (`resolveAdminCreateEntryDecision`'s `hasAdminFlowSignal` check).
 *
 * Lives in this path-only module (not `admin-dash/_logic/navigation.ts`, where it originated)
 * because it's also called from `EventsPageContent`, a client component shared with the customer
 * Events page — that file's own icon-laden nav config would otherwise ship in the customer bundle
 * for a 3-line string builder. `navigation.ts` re-exports this for its own existing callers.
 *
 * @param orgId - `enterprise_organizations.id` of the org already in scope, or `null`.
 * @returns The href for the button.
 */
export function adminCreateEventHref(orgId: number | null): string {
  return orgId != null
    ? `${ADMIN_CREATE_EVENT_PATH}?${ADMIN_ORG_ID_PARAM}=${orgId}`
    : `${ADMIN_CREATE_EVENT_PATH}?${ADMIN_LAUNCH_PARAM}=1`;
}

/** Finances' home inside the shell. Redirects to {@link ADMIN_FINANCES_DEFAULT_TAB}. */
export const ADMIN_FINANCES_PATH = `${ADMIN_V2_ROOT}/finances`;

/**
 * Finances tab ids, each its own static route folder under `/admin/v2/finances` — mirroring
 * Metrics' one-folder-per-screen shape. Each ports in a legacy `/admin/*` page's component
 * directly rather than linking out to it, so an operator never leaves the shell.
 *
 * "reconciliation" is the old `/admin/finances` page, kept in the tool's own nav under the label
 * "Finances" — its route id is spelled out instead so the URL doesn't read `/finances/finances`.
 */
export const ADMIN_FINANCES_TABS = ["accounting", "reconciliation", "metrics-2", "sow"] as const;

/** A valid `/admin/v2/finances/[tab]` segment. */
export type AdminFinancesTab = (typeof ADMIN_FINANCES_TABS)[number];

/** Tab the Finances base route redirects to. */
export const ADMIN_FINANCES_DEFAULT_TAB: AdminFinancesTab = "accounting";

/** Where `/admin/v2` sends the operator — the admin equivalent of the customer Events landing. */
export const ADMIN_V2_DEFAULT_PATH = ADMIN_EVENTS_PATH;

// ---------------------------------------------------------------------------
// Org scope — templated hrefs (`{orgId}` resolved later)
// ---------------------------------------------------------------------------

/** Org overview href template. */
export const ADMIN_ORG_HREF = `${ADMIN_V2_ROOT}/orgs/{orgId}`;

/** Org Events href template. */
export const ADMIN_ORG_EVENTS_HREF = `${ADMIN_ORG_HREF}/events`;

/**
 * Where "go to this org" lands (org switch fallback, a newly created org, the org breadcrumb).
 * The org Overview is still a placeholder, so this is the org's events list; point it back at
 * {@link ADMIN_ORG_HREF} when the Overview ships.
 */
export const ADMIN_ORG_LANDING_HREF = ADMIN_ORG_EVENTS_HREF;

/** Org Users href template. */
export const ADMIN_ORG_USERS_HREF = `${ADMIN_ORG_HREF}/users`;

/** Org Approvals href template. */
export const ADMIN_ORG_APPROVALS_HREF = `${ADMIN_ORG_HREF}/approvals`;

/** Org Reporting href template. */
export const ADMIN_ORG_REPORTING_HREF = `${ADMIN_ORG_HREF}/reporting`;

/** Org Settings href template, without a tab segment. */
export const ADMIN_ORG_SETTINGS_BASE_HREF = `${ADMIN_ORG_HREF}/settings`;

/**
 * The org Settings tab page written with Next.js dynamic segment names, for
 * `revalidatePath(pattern, "page")`. Distinct from {@link ADMIN_ORG_SETTINGS_BASE_HREF}, whose
 * `{orgId}` placeholder is this module's own templating convention and means nothing to Next.
 */
export const ADMIN_ORG_SETTINGS_ROUTE_PATTERN = `${ADMIN_V2_ROOT}/orgs/[org_id]/settings/[tab]`;

// ---------------------------------------------------------------------------
// Event scope — templated hrefs (`{eventId}` resolved later)
// ---------------------------------------------------------------------------

/** Event overview href template. */
export const ADMIN_EVENT_HREF = `${ADMIN_V2_ROOT}/events/{eventId}`;

/** Event Tasks href template. */
export const ADMIN_EVENT_TASKS_HREF = `${ADMIN_EVENT_HREF}/tasks`;

/** Event Details href template. */
export const ADMIN_EVENT_DETAILS_HREF = `${ADMIN_EVENT_HREF}/details`;

/** Event Notes href template. */
export const ADMIN_EVENT_NOTES_HREF = `${ADMIN_EVENT_HREF}/notes`;

/** Event sourcing href template, without a tab segment. */
export const ADMIN_EVENT_SOURCE_BASE_HREF = `${ADMIN_EVENT_HREF}/source`;

/** Event Finances href template — Add Charge and Credit Card live here (ENG-1065/1067/1068/1070). */
export const ADMIN_EVENT_FINANCES_BASE_HREF = `${ADMIN_EVENT_HREF}/finances`;

/** Event registration href template, without a tab segment. Redirects to the forms tab. */
export const ADMIN_EVENT_REGISTRATION_BASE_HREF = `${ADMIN_EVENT_HREF}/manage/registration`;

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

/**
 * Org Settings tab ids, mirroring the customer org settings drill-down so both surfaces stay on
 * one vocabulary. Written as string literals — route segments are strings, and a literal union is
 * what `params` validation and href building actually need — but `satisfies` pins every entry to a
 * real `OrganizationTab` value, so a customer rename breaks the build here instead of shipping a
 * silent 404.
 */
export const ADMIN_ORG_SETTINGS_TABS = [
  "profile",
  "office-location",
  "preferred-venues",
  "blacklisted-venues",
  "policies",
  "contracts",
  "integrations",
] as const satisfies readonly `${OrganizationTab}`[];

/** A valid `/admin/v2/orgs/[org_id]/settings/[tab]` segment. */
export type AdminOrgSettingsTab = (typeof ADMIN_ORG_SETTINGS_TABS)[number];

/** Tab the org Settings route defaults to. */
export const ADMIN_ORG_SETTINGS_DEFAULT_TAB: AdminOrgSettingsTab = "profile";

/** Event sourcing tab ids, in rail order. */
export const ADMIN_EVENT_SOURCE_TABS = ["venues", "search", "proposals", "contracts", "payments"] as const;

/** A valid `/admin/v2/events/[event_id]/source/<tab>` segment. */
export type AdminEventSourceTab = (typeof ADMIN_EVENT_SOURCE_TABS)[number];

/**
 * Event Finances drill-down tab ids, in rail order (ENG-1065/1070). Distinct from the unrelated
 * all-orgs `AdminFinancesTab`/`ADMIN_FINANCES_TABS` above (`/admin/v2/finances/[tab]`) — that's a
 * different, org-scoped tool (accounting list, SOW, reconciliation, subscription contracts), not
 * this per-event drill-down.
 */
export const ADMIN_EVENT_FINANCES_TABS = [
  "overview",
  "charges-card",
  "accounts-receivable",
  "cash-flows",
  "folio-reconciliation",
  "service-contracts",
  "documents",
] as const;

/** A valid `/admin/v2/events/[event_id]/finances/<tab>` segment. */
export type AdminEventFinancesTab = (typeof ADMIN_EVENT_FINANCES_TABS)[number];

/** Tab the event Finances base route redirects to. */
export const ADMIN_EVENT_FINANCES_DEFAULT_TAB: AdminEventFinancesTab = "overview";

/**
 * Registration tabs the admin surface exposes: the customer's registration tabs minus `overview`,
 * which is a customer-only landing the admin rail has no equivalent for.
 */
export const ADMIN_REGISTRATION_TABS = ENTERPRISE_REGISTRATION_TABS.filter((tab) => tab.id !== "overview");

/** A valid `/admin/v2/events/[event_id]/manage/registration/[tab]` segment. */
export type AdminRegistrationTab = Exclude<EnterpriseRegistrationTab, "overview">;

/** Tab the Registration base route redirects to — where the admin's registration work starts. */
export const ADMIN_REGISTRATION_DEFAULT_TAB: AdminRegistrationTab = "forms";

// ---------------------------------------------------------------------------
// Placeholder resolution
// ---------------------------------------------------------------------------

/** Ids a templated admin href can be resolved against. */
export interface AdminHrefIds {
  orgId?: number | string | null;
  eventId?: number | string | null;
  userId?: number | string | null;
}

/**
 * Resolves the `{orgId}`, `{eventId}` and `{userId}` placeholders in a templated admin href. An
 * href with no placeholders comes back untouched, so nav configs can mix static and templated
 * entries freely. A placeholder with no matching id is left in place rather than resolved to
 * "null" or "undefined", so a broken link is visible instead of silently pointing at a bogus
 * record.
 *
 * @param href - A templated href, e.g. {@link ADMIN_ORG_EVENTS_HREF}.
 * @param ids - The ids to substitute; omit or pass null to leave that placeholder alone.
 * @returns The href with every resolvable placeholder replaced.
 */
export function replaceAdminIds(href: string, ids: AdminHrefIds = {}): string {
  let resolved = href;
  if (ids.orgId !== undefined && ids.orgId !== null) {
    resolved = resolved.replaceAll("{orgId}", String(ids.orgId));
  }
  if (ids.eventId !== undefined && ids.eventId !== null) {
    resolved = resolved.replaceAll("{eventId}", String(ids.eventId));
  }
  if (ids.userId !== undefined && ids.userId !== null) {
    resolved = resolved.replaceAll("{userId}", String(ids.userId));
  }
  return resolved;
}

// ---------------------------------------------------------------------------
// Org-scope builders
// ---------------------------------------------------------------------------

/**
 * Concrete org overview path.
 *
 * @param orgId - The enterprise org to link to.
 * @returns e.g. `/admin/v2/orgs/5`.
 */
export function adminOrgPath(orgId: number): string {
  return replaceAdminIds(ADMIN_ORG_HREF, { orgId });
}

/**
 * Concrete org landing path — see {@link ADMIN_ORG_LANDING_HREF}.
 *
 * @param orgId - The enterprise org to link to.
 * @returns e.g. `/admin/v2/orgs/5/events` while the org Overview is disabled.
 */
export function adminOrgLandingPath(orgId: number): string {
  return replaceAdminIds(ADMIN_ORG_LANDING_HREF, { orgId });
}

/**
 * Concrete org Events path.
 *
 * @param orgId - The enterprise org to link to.
 * @returns e.g. `/admin/v2/orgs/5/events`.
 */
export function adminOrgEventsPath(orgId: number): string {
  return replaceAdminIds(ADMIN_ORG_EVENTS_HREF, { orgId });
}

/**
 * Concrete org Users path.
 *
 * @param orgId - The enterprise org to link to.
 * @returns e.g. `/admin/v2/orgs/5/users`.
 */
export function adminOrgUsersPath(orgId: number): string {
  return replaceAdminIds(ADMIN_ORG_USERS_HREF, { orgId });
}

/**
 * Concrete org Approvals path.
 *
 * @param orgId - The enterprise org to link to.
 * @returns e.g. `/admin/v2/orgs/5/approvals`.
 */
export function adminOrgApprovalsPath(orgId: number): string {
  return replaceAdminIds(ADMIN_ORG_APPROVALS_HREF, { orgId });
}

/**
 * Concrete org Reporting path.
 *
 * @param orgId - The enterprise org to link to.
 * @returns e.g. `/admin/v2/orgs/5/reporting`.
 */
export function adminOrgReportingPath(orgId: number): string {
  return replaceAdminIds(ADMIN_ORG_REPORTING_HREF, { orgId });
}

/**
 * Templated org Settings href for one tab, for static nav configs.
 *
 * @param tab - The settings tab; defaults to {@link ADMIN_ORG_SETTINGS_DEFAULT_TAB}.
 * @returns e.g. `/admin/v2/orgs/{orgId}/settings/policies`.
 */
export function adminOrgSettingsHref(tab: AdminOrgSettingsTab = ADMIN_ORG_SETTINGS_DEFAULT_TAB): string {
  return `${ADMIN_ORG_SETTINGS_BASE_HREF}/${tab}`;
}

/**
 * Concrete org Settings path for one tab.
 *
 * @param orgId - The enterprise org to link to.
 * @param tab - The settings tab; defaults to {@link ADMIN_ORG_SETTINGS_DEFAULT_TAB}.
 * @returns e.g. `/admin/v2/orgs/5/settings/policies`.
 */
export function adminOrgSettingsPath(orgId: number, tab: AdminOrgSettingsTab = ADMIN_ORG_SETTINGS_DEFAULT_TAB): string {
  return replaceAdminIds(adminOrgSettingsHref(tab), { orgId });
}

/**
 * Type guard for an org Settings `[tab]` segment, so the route can render not-found on anything
 * that isn't a real tab instead of an empty screen.
 *
 * @param value - The raw segment from `params`.
 * @returns True when the segment is a known settings tab.
 */
export function isAdminOrgSettingsTab(value: string): value is AdminOrgSettingsTab {
  return (ADMIN_ORG_SETTINGS_TABS as readonly string[]).includes(value);
}

/**
 * Concrete Users detail path.
 *
 * @param userId - The user to link to.
 * @returns e.g. `/admin/v2/users/482`.
 */
export function adminUserPath(userId: number): string {
  return replaceAdminIds(ADMIN_USER_HREF, { userId });
}

/**
 * Concrete Metrics path for one tab's static route folder. Takes no id — Metrics has no org or
 * event scope — so unlike the templated hrefs above this is the only form the link needs.
 *
 * @param tab - The metrics tab; defaults to {@link ADMIN_METRICS_DEFAULT_TAB}.
 * @returns e.g. `/admin/v2/metrics/pipeline`.
 */
export function adminMetricsPath(tab: AdminMetricsTab = ADMIN_METRICS_DEFAULT_TAB): string {
  return `${ADMIN_METRICS_PATH}/${tab}`;
}

/**
 * Concrete path for one tab in Metrics' Venues section.
 *
 * @param tab - The Venues tab; defaults to the first one, Messaging.
 * @returns e.g. `/admin/v2/metrics/venues/portal-claims`.
 * @throws Never.
 */
export function adminMetricsVenuesPath(tab: AdminMetricsVenuesTab = ADMIN_METRICS_VENUES_TABS[0]): string {
  return `${ADMIN_METRICS_VENUES_PATH}/${tab}`;
}

/** The planner bot's turn viewer, which is the one screen nested under a metrics tab. */
export const ADMIN_PLANNER_TURNS_PATH = `${ADMIN_METRICS_PATH}/planner-bot/turns`;

/**
 * Concrete path for one planner bot turn.
 *
 * @param turnId - The turn, as `nowadays.planner_bot_turns.id` spells it.
 * @returns e.g. `/admin/v2/metrics/planner-bot/turns/4821`.
 */
export function adminPlannerTurnPath(turnId: string | number): string {
  return `${ADMIN_PLANNER_TURNS_PATH}/${turnId}`;
}

/**
 * Concrete Planning path for one tab's static route folder. Takes no id — Planning has no org or
 * event scope — mirroring {@link adminMetricsPath}.
 *
 * @param tab - The planning tab; defaults to {@link ADMIN_PLANNING_DEFAULT_TAB}.
 * @returns e.g. `/admin/v2/planning/readiness`.
 */
export function adminPlanningPath(tab: AdminPlanningTab = ADMIN_PLANNING_DEFAULT_TAB): string {
  return `${ADMIN_PLANNING_PATH}/${tab}`;
}

/**
 * Concrete Finances path for one tab's static route folder. Takes no id — Finances has no org or
 * event scope — mirroring {@link adminMetricsPath}.
 *
 * @param tab - The finances tab; defaults to {@link ADMIN_FINANCES_DEFAULT_TAB}.
 * @returns e.g. `/admin/v2/finances/accounting`.
 */
export function adminFinancesPath(tab: AdminFinancesTab = ADMIN_FINANCES_DEFAULT_TAB): string {
  return `${ADMIN_FINANCES_PATH}/${tab}`;
}

/**
 * Resolves a path for a tool ported into the shell from a legacy standalone route, so its
 * internal links land in whichever mount the current page is under instead of always kicking a
 * shell operator back out to the standalone page.
 *
 * @param pathname - The current `usePathname()` value.
 * @param legacyPath - The tool's original, standalone path.
 * @param shellPath - The tool's path inside the `/admin/v2` shell.
 * @returns `shellPath` when `pathname` is under {@link ADMIN_V2_ROOT}, otherwise `legacyPath`.
 */
export function resolveShellPath(pathname: string, legacyPath: string, shellPath: string): string {
  return pathname.startsWith(ADMIN_V2_ROOT) ? shellPath : legacyPath;
}

// ---------------------------------------------------------------------------
// Event-scope builders
// ---------------------------------------------------------------------------

/**
 * Concrete event overview path.
 *
 * @param eventId - The event to link to.
 * @returns e.g. `/admin/v2/events/42`.
 */
export function adminEventPath(eventId: number): string {
  return replaceAdminIds(ADMIN_EVENT_HREF, { eventId });
}

/**
 * Concrete event Tasks path.
 *
 * @param eventId - The event to link to.
 * @returns e.g. `/admin/v2/events/42/tasks`.
 */
export function adminEventTasksPath(eventId: number): string {
  return replaceAdminIds(ADMIN_EVENT_TASKS_HREF, { eventId });
}

/**
 * Concrete event Details path.
 *
 * @param eventId - The event to link to.
 * @returns e.g. `/admin/v2/events/42/details`.
 */
export function adminEventDetailsPath(eventId: number): string {
  return replaceAdminIds(ADMIN_EVENT_DETAILS_HREF, { eventId });
}

/**
 * Concrete event Notes path.
 *
 * @param eventId - The event to link to.
 * @returns e.g. `/admin/v2/events/42/notes`.
 */
export function adminEventNotesPath(eventId: number): string {
  return replaceAdminIds(ADMIN_EVENT_NOTES_HREF, { eventId });
}

/**
 * Templated event sourcing href for one tab, for static nav configs.
 *
 * @param tab - The sourcing tab.
 * @returns e.g. `/admin/v2/events/{eventId}/source/proposals`.
 */
export function adminEventSourceHref(tab: AdminEventSourceTab): string {
  return `${ADMIN_EVENT_SOURCE_BASE_HREF}/${tab}`;
}

/**
 * Concrete event sourcing path for one tab.
 *
 * @param eventId - The event to link to.
 * @param tab - The sourcing tab.
 * @returns e.g. `/admin/v2/events/42/source/proposals`.
 */
export function adminEventSourcePath(eventId: number, tab: AdminEventSourceTab): string {
  return replaceAdminIds(adminEventSourceHref(tab), { eventId });
}

/**
 * Templated event Registration href for one tab, for static nav configs.
 *
 * @param tab - The registration tab; defaults to {@link ADMIN_REGISTRATION_DEFAULT_TAB}.
 * @returns e.g. `/admin/v2/events/{eventId}/manage/registration/forms`.
 */
export function adminEventRegistrationHref(tab: AdminRegistrationTab = ADMIN_REGISTRATION_DEFAULT_TAB): string {
  return `${ADMIN_EVENT_REGISTRATION_BASE_HREF}/${tab}`;
}

/**
 * Concrete event Registration path for one tab.
 *
 * @param eventId - The event to link to.
 * @param tab - The registration tab; defaults to {@link ADMIN_REGISTRATION_DEFAULT_TAB}.
 * @returns e.g. `/admin/v2/events/42/manage/registration/forms`.
 */
export function adminEventRegistrationPath(
  eventId: number,
  tab: AdminRegistrationTab = ADMIN_REGISTRATION_DEFAULT_TAB
): string {
  return replaceAdminIds(adminEventRegistrationHref(tab), { eventId });
}

/**
 * Type guard for a Registration `[tab]` segment. Rejects `overview`, which is a customer-only tab
 * with no admin screen behind it, as well as anything unknown.
 *
 * @param value - The raw segment from `params`.
 * @returns True when the segment is a registration tab the admin surface exposes.
 */
export function isAdminRegistrationTab(value: string): value is AdminRegistrationTab {
  return ADMIN_REGISTRATION_TABS.some((tab) => tab.id === value);
}

/**
 * Templated event Finances href for one tab, for static nav configs.
 *
 * @param tab - The finances tab; defaults to {@link ADMIN_EVENT_FINANCES_DEFAULT_TAB}.
 * @returns e.g. `/admin/v2/events/{eventId}/finances/overview`.
 */
export function adminEventFinancesHref(tab: AdminEventFinancesTab = ADMIN_EVENT_FINANCES_DEFAULT_TAB): string {
  return `${ADMIN_EVENT_FINANCES_BASE_HREF}/${tab}`;
}

/**
 * Concrete event Finances path for one tab.
 *
 * @param eventId - The event to link to.
 * @param tab - The finances tab; defaults to {@link ADMIN_EVENT_FINANCES_DEFAULT_TAB}.
 * @returns e.g. `/admin/v2/events/42/finances/overview`.
 */
export function adminEventFinancesPath(
  eventId: number,
  tab: AdminEventFinancesTab = ADMIN_EVENT_FINANCES_DEFAULT_TAB
): string {
  return replaceAdminIds(adminEventFinancesHref(tab), { eventId });
}

/**
 * Type guard for an event Finances `[tab]` segment.
 *
 * @param value - The raw segment from `params`.
 * @returns True when the segment is a finances tab the admin surface exposes.
 */
export function isAdminEventFinancesTab(value: string): value is AdminEventFinancesTab {
  return ADMIN_EVENT_FINANCES_TABS.some((tab) => tab === value);
}

// ---------------------------------------------------------------------------
// Route map
// ---------------------------------------------------------------------------

/**
 * Every concrete route pattern under `/admin/v2`, written with the Next.js dynamic segment names
 * so `src/app/admin/v2/_tests/route-map.test.ts` can walk the filesystem and assert the two match
 * exactly. Adding a route here without a `page.tsx` (or the reverse) fails that test.
 */
export const ADMIN_DASH_ROUTES = [
  ADMIN_V2_ROOT,
  `${ADMIN_V2_ROOT}/orgs`,
  `${ADMIN_V2_ROOT}/orgs/[org_id]`,
  `${ADMIN_V2_ROOT}/orgs/[org_id]/approvals`,
  `${ADMIN_V2_ROOT}/orgs/[org_id]/events`,
  `${ADMIN_V2_ROOT}/orgs/[org_id]/reporting`,
  ADMIN_ORG_SETTINGS_ROUTE_PATTERN,
  `${ADMIN_V2_ROOT}/orgs/[org_id]/users`,
  `${ADMIN_V2_ROOT}/events`,
  `${ADMIN_V2_ROOT}/events/triage`,
  `${ADMIN_V2_ROOT}/events/[event_id]`,
  `${ADMIN_V2_ROOT}/events/[event_id]/details`,
  `${ADMIN_V2_ROOT}/events/[event_id]/finances`,
  `${ADMIN_V2_ROOT}/events/[event_id]/finances/[tab]`,
  `${ADMIN_V2_ROOT}/events/[event_id]/manage/registration`,
  `${ADMIN_V2_ROOT}/events/[event_id]/manage/registration/[tab]`,
  `${ADMIN_V2_ROOT}/events/[event_id]/notes`,
  `${ADMIN_V2_ROOT}/events/[event_id]/source/contracts`,
  `${ADMIN_V2_ROOT}/events/[event_id]/source/contracts/[contract_id]`,
  `${ADMIN_V2_ROOT}/events/[event_id]/source/payments`,
  `${ADMIN_V2_ROOT}/events/[event_id]/source/proposals`,
  `${ADMIN_V2_ROOT}/events/[event_id]/source/search`,
  `${ADMIN_V2_ROOT}/events/[event_id]/source/search/hotel`,
  `${ADMIN_V2_ROOT}/events/[event_id]/source/search/restaurant`,
  `${ADMIN_V2_ROOT}/events/[event_id]/source/search/event_space`,
  `${ADMIN_V2_ROOT}/events/[event_id]/source/search/experience`,
  `${ADMIN_V2_ROOT}/events/[event_id]/source/venues`,
  `${ADMIN_V2_ROOT}/events/[event_id]/tasks`,
  `${ADMIN_V2_ROOT}/users`,
  `${ADMIN_V2_ROOT}/users/[user_id]`,
  `${ADMIN_V2_ROOT}/tasks`,
  `${ADMIN_V2_ROOT}/archive`,
  `${ADMIN_V2_ROOT}/finances`,
  `${ADMIN_V2_ROOT}/finances/accounting`,
  `${ADMIN_V2_ROOT}/finances/reconciliation`,
  `${ADMIN_V2_ROOT}/finances/metrics-2`,
  `${ADMIN_V2_ROOT}/finances/sow`,
  `${ADMIN_V2_ROOT}/finances/sow/[id]`,
  `${ADMIN_V2_ROOT}/finances/subscription-contracts`,
  `${ADMIN_V2_ROOT}/finances/subscription-contracts/[id]`,
  `${ADMIN_V2_ROOT}/finances/subscription-contracts/[id]/invoice`,
  `${ADMIN_V2_ROOT}/finances/accounting/backfill`,
  `${ADMIN_V2_ROOT}/finances/investor-report`,
  `${ADMIN_V2_ROOT}/finances/invoice`,
  `${ADMIN_V2_ROOT}/metrics`,
  `${ADMIN_V2_ROOT}/metrics/finance`,
  `${ADMIN_V2_ROOT}/metrics/pipeline`,
  `${ADMIN_V2_ROOT}/metrics/self-serve`,
  `${ADMIN_V2_ROOT}/metrics/extractions`,
  `${ADMIN_V2_ROOT}/metrics/messaging`,
  `${ADMIN_V2_ROOT}/metrics/venues`,
  `${ADMIN_V2_ROOT}/metrics/venues/messaging`,
  `${ADMIN_V2_ROOT}/metrics/venues/portal-claims`,
  `${ADMIN_V2_ROOT}/metrics/planner-bot`,
  `${ADMIN_V2_ROOT}/metrics/planner-bot/turns`,
  `${ADMIN_V2_ROOT}/metrics/planner-bot/turns/[turn_id]`,
  `${ADMIN_V2_ROOT}/metrics/fetch`,
  `${ADMIN_V2_ROOT}/planning`,
  `${ADMIN_V2_ROOT}/planning/attention`,
  `${ADMIN_V2_ROOT}/planning/readiness`,
  `${ADMIN_V2_ROOT}/planning/people`,
] as const;

/**
 * Parses a positive-integer route id out of a raw `params` segment. `Number()` rather than
 * `parseInt` so `"12abc"` is `NaN` instead of `12`; the exponent form `"1e3"` is rejected by the
 * digits-only guard so a link can't quietly resolve to a different record than it reads as.
 *
 * @param value - The raw segment from `params`, or undefined when absent.
 * @returns The parsed id, or null when the segment is not a positive integer.
 */
export function parseAdminRouteId(value: string | undefined): number | null {
  if (!value || !/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed <= 0) return null;
  return parsed;
}
